/**
 * Visual tokens for the economy pages.
 *
 * Everything resolves to the app's shared colour tokens (`src/styles/color-tokens.ts`), so
 * the pod reads as part of the reporting pages rather than carrying the budget prototype's
 * own neutral `#1a1a1a` palette. Surfaces, cards and form controls come from the toolkit
 * components themselves — what is left here is the spacing and typography that has no
 * toolkit primitive, plus the nav classes copied from the reporting menu and the Aktører
 * tab bar so the two stay in step.
 */

/** Rail width in px — the same column the reporting pages use. */
export const ECONOMY_SIDEBAR_WIDTH = 320;

/* Typography ------------------------------------------------------------- */

/** Section eyebrow, matching the reports menu's group headings. */
export const ECONOMY_LABEL_CLASS = 'text-xs font-semibold uppercase tracking-wider text-white/50';

export const ECONOMY_ROW_LABEL_CLASS = 'text-sm font-normal text-secondary';

/**
 * A list nested inside one row's value keeps the card's own row rhythm: 12px between
 * rows, each row 24px tall. Flex drops the inline leading that makes a plain text row
 * 24px rather than 20px, so the height has to be asked for.
 */
export const ECONOMY_STACK_CLASS = 'flex flex-col gap-3';
export const ECONOMY_STACK_ROW_CLASS = 'flex min-h-6 items-center';
export const ECONOMY_VALUE_CLASS = 'text-sm text-primary';
export const ECONOMY_HINT_CLASS = 'text-xs text-secondary';

/* Form controls ---------------------------------------------------------- */

/**
 * Matches the agreement forms: a 38px `Input` / 36px `Select` at 14px. `Input` and
 * `Select` supply their own surface, border and error state, so nothing is overridden.
 */
export const ECONOMY_FIELD_SIZE = 'medium' as const;

export const ECONOMY_TICK_CLASS = 'h-3.5 w-3.5 accent-brand-primary';

/* Tables ----------------------------------------------------------------- */

/* Accents ---------------------------------------------------------------- */

export const ECONOMY_CHIP_OK_CLASS = 'text-xs font-medium bg-positive/[0.12] text-positive';
export const ECONOMY_CHIP_OFF_CLASS = 'text-xs font-medium bg-surface-elevated text-secondary';

/* Navigation ------------------------------------------------------------- */

/** Rail entry, copied from the reports menu so both rails read the same. */
export const ECONOMY_NAV_ITEM_CLASS =
  'flex items-center gap-x-3 border-l-2 border-b border-border/50 py-2 pl-3 pr-2 text-sm font-medium leading-6 transition duration-150 ease-in-out';
export const ECONOMY_NAV_ITEM_ACTIVE_CLASS = 'border-l-brand-primary bg-surface-elevated text-brand-primary';

/** Tab bar, copied from the Aktører header. */
export const ECONOMY_TAB_BASE_CLASS = 'pb-2 text-sm font-medium transition-colors';
export const ECONOMY_TAB_ACTIVE_CLASS = 'border-b-2 border-brand-primary text-brand-primary';
export const ECONOMY_TAB_IDLE_CLASS = 'text-white hover:text-brand-primary';
